import { FormEvent, useEffect, useState } from 'react';
import type { CurrentMember, UiLocale } from '../../../shared/models';
import { api } from '../../api';
import { LocaleSelect, useI18n } from '../../i18n';
import { localizedError } from '../../i18n/errors';
import { useUnsavedChanges } from '../../unsaved-changes';
import { SessionCard } from './SessionCard';

interface AccountProps {
  member: CurrentMember;
  onMemberChange: (member: CurrentMember) => void;
}

export function AccountPage({ member, onMemberChange }: AccountProps) {
  const { locale, setLocale, t } = useI18n();
  const [displayName, setDisplayName] = useState(member.displayName);
  const [preferredLocale, setPreferredLocale] = useState<UiLocale>(member.preferredLocale ?? locale);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const profileDirty = displayName !== member.displayName || preferredLocale !== (member.preferredLocale ?? locale);
  useUnsavedChanges(profileDirty);

  useEffect(() => {
    setDisplayName(member.displayName);
    setPreferredLocale(member.preferredLocale ?? locale);
  }, [member, locale]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault(); setProfileError(''); setProfileMessage('');
    try {
      const result = await api.updateAccountProfile({ displayName, preferredLocale, revision: member.revision });
      onMemberChange(result.member); setLocale(preferredLocale); setProfileMessage(t('account.profileSaved'));
    } catch (error) {
      setProfileError(localizedError(error, locale, { 'zh-CN': '保存资料失败', en: 'Failed to save profile' }));
    }
  }

  return <section className="account-layout">
    <header className="section-head"><div><span className="eyebrow">Account</span><h2>{t('account.title')}</h2><p>{t('account.subtitle')}</p></div></header>
    <form className="account-card" onSubmit={saveProfile}>
      <div><span className="eyebrow">Profile</span><h3>{t('account.profile')}</h3></div>
      <label>{t('account.displayName')}<input maxLength={120} value={displayName} onChange={event => { setDisplayName(event.target.value); setProfileMessage(''); }} required /></label>
      <label>{t('common.language')}
        <select value={preferredLocale} onChange={event => {
          setPreferredLocale(event.target.value as UiLocale);
          setProfileMessage('');
        }}>
          <option value="zh-CN">中文</option><option value="en">English</option>
        </select>
      </label>
      {profileDirty && <p className="field-help">{locale === 'zh-CN' ? '有未保存的账号资料修改。界面语言会在保存成功后正式切换。' : 'You have unsaved account changes. The interface language changes after a successful save.'}</p>}
      {profileError && <p className="form-error">{profileError}</p>}
      {profileMessage && <p className="form-success">{profileMessage}</p>}
      <button className="primary-button compact" disabled={!profileDirty}>{t('account.saveProfile')}</button>
    </form>
    <PasswordCard onMemberChange={onMemberChange} />
    <SessionCard />
  </section>;
}

function PasswordCard({ onMemberChange }: Pick<AccountProps, 'onMemberChange'>) {
  const { locale, t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const passwordDirty = Boolean(currentPassword || newPassword || confirmPassword);
  useUnsavedChanges(passwordDirty);

  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(''); setMessage('');
    try {
      const result = await api.changePassword({ currentPassword, newPassword, confirmPassword });
      onMemberChange(result.member); setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setMessage(t('account.passwordChanged'));
    } catch (err) {
      setError(localizedError(err, locale, { 'zh-CN': '修改密码失败', en: 'Failed to change password' }));
    } finally { setBusy(false); }
  }

  return <form className="account-card" onSubmit={submit}>
    <div><span className="eyebrow">Security</span><h3>{t('account.passwordTitle')}</h3><p>{t('account.passwordHelp')}</p></div>
    <label>{t('account.currentPassword')}<input type="password" autoComplete="current-password" value={currentPassword} onChange={event => { setCurrentPassword(event.target.value); setMessage(''); }} required /></label>
    <label>{t('account.newPassword')}<input type="password" autoComplete="new-password" minLength={12} maxLength={200} value={newPassword} onChange={event => { setNewPassword(event.target.value); setMessage(''); }} required /></label>
    <label>{t('account.confirmPassword')}<input type="password" autoComplete="new-password" minLength={12} maxLength={200} value={confirmPassword} onChange={event => { setConfirmPassword(event.target.value); setMessage(''); }} required /></label>
    {error && <p className="form-error">{error}</p>}
    {message && <p className="form-success">{message}</p>}
    <button className="primary-button compact" disabled={busy || !passwordDirty}>{t('account.changePassword')}</button>
  </form>;
}

export function ForcedPasswordChangePage({ member: _member, onMemberChange, onLogout }: AccountProps & { onLogout: () => void }) {
  const { t } = useI18n();
  return <main className="forced-password-layout">
    <section className="forced-password-card">
      <div className="forced-password-top"><span className="eyebrow">Lanshan · Menu Lab</span><LocaleSelect /></div>
      <h1>{t('account.forcedTitle')}</h1><p>{t('account.forcedBody')}</p>
      <PasswordCard onMemberChange={onMemberChange} />
      <button className="text-button" type="button" onClick={onLogout}>{t('common.logout')}</button>
    </section>
  </main>;
}
